import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { PROJECTS_PAGE_ROUTE } from "@/shared/utils";
import type { RangeValue } from "@/shared/ui";
import { useGetProjectsQuery, useGetMyProjectsQuery } from "../api";
import type { Project, ProjectStatus, ProjectCategory } from "../types";

type ViewMode = "list" | "grid";

export type ProjectSortOption = "default" | "name_asc" | "name_desc" | "budget_desc" | "budget_asc" | "date_desc" | "date_asc";

export function useProjectsPage() {
  const { t, i18n } = useTranslation("ProjectsPage");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<ProjectSortOption>("default");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Set<ProjectCategory>>(new Set());
  const [selectedRegions, setSelectedRegions] = useState<Set<number>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<Set<ProjectStatus>>(new Set());
  const [dateRange, setDateRange] = useState<RangeValue | null>(null);
  const [coFinancing, setCoFinancing] = useState(false);
  const [budgetFrom, setBudgetFrom] = useState("");
  const [budgetTo, setBudgetTo] = useState("");

  const canEditProject = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);
  const allQuery = useGetProjectsQuery(undefined, { skip: !canEditProject });
  const myQuery = useGetMyProjectsQuery(undefined, { skip: canEditProject });
  const { data: projects, isLoading, isFetching, error } = canEditProject ? allQuery : myQuery;

  const maxBudget = useMemo(() => {
    if (!projects || projects.length === 0) return 0;
    return Math.ceil(
      projects.reduce((max, p) => {
        const b = p.budget ? parseFloat(p.budget) : 0;
        return b > max ? b : max;
      }, 0),
    );
  }, [projects]);

  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [waitingForRefetch, setWaitingForRefetch] = useState(false);
  const [successAction, setSuccessAction] = useState<"projectUpdated" | "projectCreated" | null>(null);
  const [removalProjectId, setRemovalProjectId] = useState<number | null>(null);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    let result = projects;

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(query));
    }

    if (selectedCategories.size > 0) {
      result = result.filter((p) => p.category && selectedCategories.has(p.category));
    }

    if (selectedRegions.size > 0) {
      result = result.filter((p) => p.region && selectedRegions.has(p.region.id));
    }

    if (selectedStatuses.size > 0) {
      result = result.filter((p) => selectedStatuses.has(p.status));
    }

    if (budgetFrom || budgetTo) {
      const from = budgetFrom ? parseFloat(budgetFrom) : 0;
      const to = budgetTo ? parseFloat(budgetTo) : Infinity;
      result = result.filter((p) => {
        if (!p.budget) return false;
        const b = parseFloat(p.budget);
        return b >= from && b <= to;
      });
    }

    if (dateRange?.start || dateRange?.end) {
      result = result.filter((p) => {
        if (!p.start_date && !p.end_date) return false;
        const pStart = p.start_date ? new Date(p.start_date) : null;
        const pEnd = p.end_date ? new Date(p.end_date) : null;
        const fStart = dateRange.start ?? null;
        const fEnd = dateRange.end ?? null;

        if (fStart && pEnd && pEnd < fStart) return false;
        if (fEnd && pStart && pStart > fEnd) return false;
        return true;
      });
    }

    return result;
  }, [projects, searchQuery, selectedCategories, selectedRegions, selectedStatuses, budgetFrom, budgetTo, dateRange]);

  const sortedProjects = useMemo(() => {
    const locale = i18n.language;
    return [...filteredProjects].sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name, locale);
        case "name_desc":
          return b.name.localeCompare(a.name, locale);
        case "budget_desc": {
          const bA = a.budget ? parseFloat(a.budget) : null;
          const bB = b.budget ? parseFloat(b.budget) : null;
          if (bA === null && bB === null) return 0;
          if (bA === null) return 1;
          if (bB === null) return -1;
          return bB - bA;
        }
        case "budget_asc": {
          const bA = a.budget ? parseFloat(a.budget) : null;
          const bB = b.budget ? parseFloat(b.budget) : null;
          if (bA === null && bB === null) return 0;
          if (bA === null) return 1;
          if (bB === null) return -1;
          return bA - bB;
        }
        case "date_desc": {
          const dA = a.start_date ? new Date(a.start_date).getTime() : null;
          const dB = b.start_date ? new Date(b.start_date).getTime() : null;
          if (dA === null && dB === null) return 0;
          if (dA === null) return 1;
          if (dB === null) return -1;
          return dB - dA;
        }
        case "date_asc": {
          const dA = a.start_date ? new Date(a.start_date).getTime() : null;
          const dB = b.start_date ? new Date(b.start_date).getTime() : null;
          if (dA === null && dB === null) return 0;
          if (dA === null) return 1;
          if (dB === null) return -1;
          return dA - dB;
        }
        case "default":
        default:
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
    });
  }, [filteredProjects, sortBy, i18n.language]);

  if (waitingForRefetch && !isFetching) {
    setWaitingForRefetch(false);
    if (isEditOpen) {
      setIsEditOpen(false);
      setSelectedProject(null);
      setSuccessAction("projectUpdated");
    }
    if (isCreateOpen) {
      setIsCreateOpen(false);
      setSuccessAction("projectCreated");
    }
  }

  function handleProjectClick(projectId: number) {
    navigate(`${PROJECTS_PAGE_ROUTE}/${projectId}`);
  }

  function handleEditProject(project: Project) {
    setSelectedProject(project);
    setIsEditOpen(true);
  }

  function handleCloseEdit() {
    setIsEditOpen(false);
    setSelectedProject(null);
  }

  function handleEditSuccess() {
    setWaitingForRefetch(true);
  }

  function handleOpenCreate() {
    setIsCreateOpen(true);
  }

  function handleCloseCreate() {
    setIsCreateOpen(false);
  }

  function handleCreateSuccess() {
    setWaitingForRefetch(true);
  }

  function handleCloseSuccessPrompt() {
    setSuccessAction(null);
  }

  function handleRequestRemovalFromEdit() {
    if (!selectedProject) return;
    const projectId = selectedProject.id;
    setIsEditOpen(false);
    setRemovalProjectId(projectId);
  }

  function handleCloseRemoval() {
    setRemovalProjectId(null);
  }

  function handleCategoryToggle(category: ProjectCategory) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  function handleRegionToggle(regionId: number) {
    setSelectedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  }

  function handleStatusToggle(status: ProjectStatus) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  function handleCoFinancingToggle() {
    setCoFinancing((prev) => !prev);
  }

  function handleResetFilters() {
    setSearchQuery("");
    setSelectedCategories(new Set());
    setSelectedRegions(new Set());
    setSelectedStatuses(new Set());
    setDateRange(null);
    setCoFinancing(false);
    setBudgetFrom("");
    setBudgetTo("");
  }

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    selectedCategories.size > 0 ||
    selectedRegions.size > 0 ||
    selectedStatuses.size > 0 ||
    dateRange !== null ||
    coFinancing ||
    budgetFrom !== "" ||
    budgetTo !== "";

  return {
    t,
    projects: sortedProjects,
    isLoading,
    error,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    selectedRegions,
    handleRegionToggle,
    selectedStatuses,
    handleStatusToggle,
    dateRange,
    setDateRange,
    coFinancing,
    handleCoFinancingToggle,
    budgetFrom,
    setBudgetFrom,
    budgetTo,
    setBudgetTo,
    maxBudget,
    handleProjectClick,
    pageTitle: t("meta.title"),
    selectedProject,
    isEditOpen,
    isCreateOpen,
    waitingForRefetch,
    successAction,
    canEditProject,
    handleEditProject,
    handleCloseEdit,
    handleEditSuccess,
    handleOpenCreate,
    handleCloseCreate,
    handleCreateSuccess,
    handleCloseSuccessPrompt,
    handleResetFilters,
    hasActiveFilters,
    removalProjectId,
    handleRequestRemovalFromEdit,
    handleCloseRemoval,
  };
}
